import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useBuilding } from "../../context/BuildingContext";
import { bankAccountsApi, bankTransactionsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import PageHeader from "../../components/common/PageHeader";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { BankAccount, BankTransaction } from "@akfdev-com/shared/src/models";
import ExplainTransactionForm from "./ExplainTransactionForm";
import ExplainedTransactionSummary from "./ExplainedTransactionSummary";
import TransactionAttachments from "./TransactionAttachments";

/**
 * Full-page "Edit an Explanation" view (Alt 3). Reached from the inline
 * explain form's "Open full page" link. Loads the bank transaction and its
 * account, then reuses ExplainTransactionForm in its `page` variant. On a
 * successful reconcile it returns to the account detail page.
 */
const ExplainTransaction: React.FC = () => {
  const { id, txId } = useParams<{ id: string; txId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentBuilding } = useBuilding();
  const buildingId =
    searchParams.get("buildingId") || currentBuilding?.id || "";

  const backToAccount = () =>
    navigate(`/dashboard/banking/${id}?buildingId=${buildingId}`);

  const { data: account, isLoading: accountLoading } = useQuery<BankAccount>({
    queryKey: ["bank-account", id, buildingId],
    queryFn: () => bankAccountsApi.get(id!, buildingId),
    enabled: !!id && !!buildingId,
  });

  const {
    data: transaction,
    isLoading: txLoading,
    error: txError,
  } = useQuery<BankTransaction>({
    queryKey: ["bank-transaction", txId, buildingId],
    queryFn: () => bankTransactionsApi.get(txId!, buildingId),
    enabled: !!txId && !!buildingId,
  });

  if (accountLoading || txLoading) return <Spinner />;
  if (txError) return <ErrorAlert message="Failed to load the transaction." />;
  if (!transaction) return <ErrorAlert message="Transaction not found." />;

  const currency = account?.currency ?? "GBP";
  const moneyIn = transaction.grossValue >= 0;

  return (
    <div>
      <PageHeader
        title="Edit an Explanation"
        description={account?.name ?? "Bank transaction"}
      />

      {/* Transaction summary */}
      <div className="mt-4 grid grid-cols-2 gap-4 rounded-lg border border-gray-200 bg-white p-5 sm:grid-cols-4">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Date
          </dt>
          <dd className="mt-1 text-sm font-semibold text-gray-900">
            {formatDate(transaction.date)}
          </dd>
        </div>
        <div className="col-span-1 sm:col-span-2">
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Description
          </dt>
          <dd className="mt-1 truncate text-sm font-semibold text-gray-900">
            {transaction.description}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {moneyIn ? "Money in" : "Money out"}
          </dt>
          <dd
            className={`mt-1 text-lg font-bold ${
              moneyIn ? "text-green-700" : "text-red-600"
            }`}
          >
            {formatCurrency(transaction.grossValue / 100, currency)}
          </dd>
        </div>
      </div>

      {transaction.reconciled ? (
        <div className="mt-6 rounded-lg border border-gray-200 bg-white p-5">
          <ExplainedTransactionSummary
            transaction={transaction}
            buildingId={buildingId}
            currency={currency}
            variant="page"
            onUnmatched={backToAccount}
          />
        </div>
      ) : (
        <div className="mt-6 rounded-lg border border-gray-200 bg-white p-5">
          <ExplainTransactionForm
            transaction={transaction}
            buildingId={buildingId}
            currency={currency}
            variant="page"
            onReconciled={backToAccount}
            onCancel={backToAccount}
          />
        </div>
      )}

      <div className="mt-6 rounded-lg border border-gray-200 bg-white p-5">
        <TransactionAttachments
          transaction={transaction}
          buildingId={buildingId}
        />
      </div>
    </div>
  );
};

export default ExplainTransaction;
